//src/Hooks/useMapFilter.js

import { useMemo, useEffect } from 'react';

// --- SYSTEM KEYS TO IGNORE IN SEARCH ---
const EXCLUDED_KEYS = new Set([
  'id',
  'icon',
  'iconID',
  'initialStyleIcon',
  'markerGroupID',
  'markerID',
  'polygonID',
  'polylineID',
  'url',
  'img',
  'hoverImage',
  'lastModified',
  'timestamp',
  'date',
  'publicFromDate',
  'file',
  'fileObject',
  'color',
  'weight',
  'isDrawing',
  'isPrivate',
  'visibility',
  'showcase',
  'categoryId',
  'regionId',
  'fileName',
  'type',
  'uploadedAt',
  'importBatchId', // AMENDED: Prevent batch IDs from being caught in text searches
]);

// Utility to escape regex characters safely for whole word matching
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const useMapFilter = (
  markersData,
  polygonsData,
  polylinesData,
  filterCriteria,
  onFilterResults,
) => {
  // Stringify the criteria to completely destroy volatile object reference loops
  // from the parent component causing infinite React re-renders.
  const filterCriteriaStr = JSON.stringify(filterCriteria || null);

  const { filteredMarkers, filteredPolygons, filteredPolylines, totalCount } =
    useMemo(() => {
      const safeMarkers = markersData || [];
      const safePolygons = polygonsData || [];
      const safePolylines = polylinesData || [];

      const getUniqueGroupCount = (arr1, arr2, arr3) => {
        const uniqueGroups = new Set();
        const addGroups = (items) => {
          items.forEach((item) => {
            const groupId = item.markerGroupID || item.id;
            if (groupId) {
              uniqueGroups.add(groupId);
            }
          });
        };
        addGroups(arr1);
        addGroups(arr2);
        addGroups(arr3);
        return uniqueGroups.size;
      };

      const parsedCriteria = JSON.parse(filterCriteriaStr);

      if (!parsedCriteria) {
        return {
          filteredMarkers: safeMarkers,
          filteredPolygons: safePolygons,
          filteredPolylines: safePolylines,
          totalCount: getUniqueGroupCount(safeMarkers, safePolygons, safePolylines),
        };
      }

      const {
        regionIds,
        categoryIds,
        dateRangeType,
        dateFrom,
        dateTo,
        keywords,
        controller,
        infoFilterA,
        infoFilterB,
        showcase,
        unpublishedBatch, // AMENDED: Destructure pulldown state
      } = parsedCriteria;

      const hasRegionFilter = regionIds && regionIds.length > 0;
      const hasCategoryFilter = categoryIds && categoryIds.length > 0;
      const hasDateFilter = dateRangeType === 'RANGE' && (dateFrom || dateTo);
      const hasKeywordFilter = keywords && keywords.trim().length > 0;
      const hasControllerFilter = controller && controller.trim().length > 0;
      const hasInfoA = infoFilterA && infoFilterA.trim().length > 0;
      const hasInfoB = infoFilterB && infoFilterB.trim().length > 0;
      const hasShowcaseFilter = showcase === true;
      const hasUnpublishedFilter = unpublishedBatch && unpublishedBatch !== 'ALL'; // AMENDED: Only filter if a specific batch is selected

      if (
        !hasRegionFilter &&
        !hasCategoryFilter &&
        !hasDateFilter &&
        !hasKeywordFilter &&
        !hasControllerFilter &&
        !hasInfoA &&
        !hasInfoB &&
        !hasShowcaseFilter &&
        !hasUnpublishedFilter // AMENDED: Include in master bypass check
      ) {
        return {
          filteredMarkers: safeMarkers,
          filteredPolygons: safePolygons,
          filteredPolylines: safePolylines,
          totalCount: getUniqueGroupCount(safeMarkers, safePolygons, safePolylines),
        };
      }

      const extractAllText = (obj) => {
        if (!obj) return '';
        if (typeof obj === 'string') {
          // Strip HTML tags and replace with a space to maintain word boundaries
          return obj.replace(/<[^>]*>?/gm, ' ').toUpperCase();
        }
        if (typeof obj === 'number' || typeof obj === 'boolean') {
          return String(obj).toUpperCase();
        }
        if (typeof obj === 'object') {
          return Object.entries(obj)
            .filter(([key]) => !EXCLUDED_KEYS.has(key))
            .map(([_, val]) => extractAllText(val))
            .join(' ');
        }
        return '';
      };

      const filterItem = (item) => {
        if (hasRegionFilter) {
          if (!item.regionId || !regionIds.includes(item.regionId)) return false;
        }
        if (hasCategoryFilter) {
          if (!item.categoryId || !categoryIds.includes(item.categoryId)) return false;
        }
        if (hasShowcaseFilter) {
          if (item.showcase !== true) return false;
        }
        // AMENDED: Apply Unpublished Markers pulldown logic
        if (hasUnpublishedFilter) {
          if (item.importBatchId !== unpublishedBatch) return false;
        }

        let allItemValues = '';
        if (hasInfoA || hasInfoB) {
          allItemValues = extractAllText(item).replace(/\s+/g, ' ');
        }

        if (hasKeywordFilter) {
          const searchTerms = keywords
            .toUpperCase()
            .split(/\s+/)
            .filter((k) => k);

          const itemKeywords = (item.keywords || '').toUpperCase().replace(/\s+/g, ' ');

          const match = searchTerms.some((term) => {
            const regex = new RegExp(`\\b${escapeRegExp(term)}\\b`);
            return regex.test(itemKeywords);
          });

          if (!match) return false;
        }

        if (hasControllerFilter) {
          const searchController = controller.trim().toUpperCase();
          const itemController = (item.controller || '').toUpperCase();
          if (!itemController.includes(searchController)) return false;
        }

        if (hasDateFilter) {
          if (!item.date) return false;
          if (dateFrom && item.date < dateFrom) return false;
          if (dateTo && item.date > dateTo) return false;
        }

        if (hasInfoA || hasInfoB) {
          let passA = true;
          if (hasInfoA) {
            const termsA = infoFilterA
              .toUpperCase()
              .split(/\s+/)
              .filter((t) => t);

            // OR logic: Passes if ANY of the words in Input A are found as whole words
            passA = termsA.some((term) => {
              const regex = new RegExp(`\\b${escapeRegExp(term)}\\b`);
              return regex.test(allItemValues);
            });
          }

          let passB = true;
          if (hasInfoB) {
            const termsB = infoFilterB
              .toUpperCase()
              .split(/\s+/)
              .filter((t) => t);

            // OR logic: Passes if ANY of the words in Input B are found as whole words
            passB = termsB.some((term) => {
              const regex = new RegExp(`\\b${escapeRegExp(term)}\\b`);
              return regex.test(allItemValues);
            });
          }

          // AND logic: Both Input A and Input B must be satisfied to keep the item
          if (!passA || !passB) return false;
        }

        return true;
      };

      const fMarkers = safeMarkers.filter(filterItem);
      const fPolygons = safePolygons.filter(filterItem);
      const fPolylines = safePolylines.filter(filterItem);

      return {
        filteredMarkers: fMarkers,
        filteredPolygons: fPolygons,
        filteredPolylines: fPolylines,
        totalCount: getUniqueGroupCount(fMarkers, fPolygons, fPolylines),
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markersData, polygonsData, polylinesData, filterCriteriaStr]);

  useEffect(() => {
    if (filterCriteria && onFilterResults) {
      onFilterResults(totalCount);
    }
  }, [filterCriteria, totalCount, onFilterResults]);

  return { filteredMarkers, filteredPolygons, filteredPolylines };
};
